/**
 * AccuracyEcosystem — Hub-and-spoke infographic.
 * Center: PETRALOC / Your Content. Five outer nodes connected via thin dotted
 * teal lines: Native Fluency, Cultural Nuance, Terminology Precision,
 * Human QA Review, AI Enhancement.
 */
const CENTER = { x: 300, y: 300 };
const ORBIT = 215; // distance from center to outer node center
const OUTER_R = 66;
const CENTER_R = 108;

const NODES = [
  { label: "Native Fluency", angle: -90, icon: "speech" },
  { label: "Cultural Nuance", angle: -18, icon: "globe" },
  { label: "Terminology Precision", angle: 54, icon: "target" },
  { label: "Human QA Review", angle: 126, icon: "shield" },
  { label: "AI Enhancement", angle: 198, icon: "sparkle" },
];

function point(angleDeg, radius) {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CENTER.x + radius * Math.cos(rad),
    y: CENTER.y + radius * Math.sin(rad),
  };
}

/* Icon paths sized for a 28×28 viewbox centred at origin (drawn inside <g transform="translate(cx, cy)"> ) */
function Icon({ name, x, y, color = "#14D5C7" }) {
  const common = {
    fill: "none",
    stroke: color,
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  return (
    <g transform={`translate(${x - 14}, ${y - 14})`}>
      {name === "speech" && (
        <g {...common}>
          <path d="M4 6h20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-8l-5 4v-4H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" />
          <line x1="8" y1="11" x2="20" y2="11" />
          <line x1="8" y1="15" x2="16" y2="15" />
        </g>
      )}
      {name === "globe" && (
        <g {...common}>
          <circle cx="14" cy="14" r="11" />
          <ellipse cx="14" cy="14" rx="5" ry="11" />
          <line x1="3" y1="14" x2="25" y2="14" />
        </g>
      )}
      {name === "target" && (
        <g {...common}>
          <circle cx="14" cy="14" r="11" />
          <circle cx="14" cy="14" r="6.5" />
          <circle cx="14" cy="14" r="2" fill={color} stroke="none" />
        </g>
      )}
      {name === "shield" && (
        <g {...common}>
          <path d="M14 3 4 7v7c0 6.2 4.3 10.5 10 12 5.7-1.5 10-5.8 10-12V7l-10-4z" />
          <path d="M9.5 14.5 13 18l5.5-6" />
        </g>
      )}
      {name === "sparkle" && (
        <g {...common}>
          <path d="M14 3v6M14 19v6M3 14h6M19 14h6" />
          <path d="m6.5 6.5 3.5 3.5M18 18l3.5 3.5M6.5 21.5 10 18M18 10l3.5-3.5" />
          <circle cx="14" cy="14" r="2.5" fill={color} stroke="none" />
        </g>
      )}
    </g>
  );
}

export default function AccuracyEcosystem() {
  return (
    <div className="w-full max-w-[600px] mx-auto" data-testid="accuracy-ecosystem">
      <svg viewBox="0 0 600 600" className="w-full h-auto block">
        <defs>
          <radialGradient id="ae-center" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stopColor="#0C255B" />
            <stop offset="60%" stopColor="#0A1F4C" />
            <stop offset="100%" stopColor="#081A3D" />
          </radialGradient>
          <radialGradient id="ae-glow" cx="50%" cy="50%" r="50%">
            <stop offset="55%" stopColor="#14D5C7" stopOpacity="0" />
            <stop offset="85%" stopColor="#14D5C7" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#14D5C7" stopOpacity="0" />
          </radialGradient>
          <filter id="ae-shadow" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow
              dx="0"
              dy="4"
              stdDeviation="6"
              floodColor="#0C255B"
              floodOpacity="0.10"
            />
          </filter>
          <filter id="ae-center-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow
              dx="0"
              dy="6"
              stdDeviation="14"
              floodColor="#0C255B"
              floodOpacity="0.32"
            />
          </filter>
        </defs>

        {/* dotted connector lines (drawn first so they sit behind nodes) */}
        {NODES.map((n, i) => {
          const outer = point(n.angle, ORBIT);
          const start = point(n.angle, CENTER_R + 4);
          const end = point(n.angle, ORBIT - OUTER_R - 4);
          return (
            <line
              key={`conn-${i}`}
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke="#14D5C7"
              strokeOpacity="0.55"
              strokeWidth="1.6"
              strokeDasharray="2 6"
              strokeLinecap="round"
            />
          );
        })}

        {/* center glow halo */}
        <circle cx={CENTER.x} cy={CENTER.y} r={CENTER_R + 26} fill="url(#ae-glow)" />

        {/* center node */}
        <g filter="url(#ae-center-shadow)">
          <circle
            cx={CENTER.x}
            cy={CENTER.y}
            r={CENTER_R}
            fill="url(#ae-center)"
            stroke="#14D5C7"
            strokeOpacity="0.5"
            strokeWidth="1.25"
          />
        </g>
        <text
          x={CENTER.x}
          y={CENTER.y - 10}
          textAnchor="middle"
          fontSize="11"
          fontWeight="700"
          fill="#14D5C7"
          letterSpacing="3.5"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          PETRALOC
        </text>
        <text
          x={CENTER.x}
          y={CENTER.y + 18}
          textAnchor="middle"
          fontSize="20"
          fontWeight="700"
          fill="#FFFFFF"
          style={{ fontFamily: "Inter, sans-serif", letterSpacing: "-0.01em" }}
        >
          Your Content
        </text>

        {/* outer nodes */}
        {NODES.map((n, i) => {
          const p = point(n.angle, ORBIT);
          const words = n.label.split(" ");
          // wrap to max 2 lines for nice fit inside r=66 circle
          let line1 = words[0];
          let line2 = words.slice(1).join(" ");
          if (words.length === 1) {
            line1 = words[0];
            line2 = "";
          }
          return (
            <g key={`node-${i}`}>
              <circle
                cx={p.x}
                cy={p.y}
                r={OUTER_R}
                fill="#FFFFFF"
                stroke="#E2E8F0"
                strokeWidth="1"
                filter="url(#ae-shadow)"
              />
              <Icon name={n.icon} x={p.x} y={p.y - 22} />
              <text
                x={p.x}
                y={p.y + 16}
                textAnchor="middle"
                fontSize="12"
                fontWeight="600"
                fill="#0C255B"
                style={{ fontFamily: "Inter, sans-serif", letterSpacing: "-0.005em" }}
              >
                <tspan x={p.x} dy="0">
                  {line1}
                </tspan>
                {line2 && (
                  <tspan x={p.x} dy="14">
                    {line2}
                  </tspan>
                )}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
